import { useMemo } from 'react';
import { useAnalysisStore, AssessmentHistoryItem } from '../../store/analysisStore';
import { computeScores } from './computeScores';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Alert,
  Stack
} from '@mui/material';

interface Props {
  data?: AssessmentHistoryItem[];
  overrideUrl?: string; // Explicitly provide URL for filtering if data is global
}

export default function AssessmentComparison({ data, overrideUrl }: Props) {
  const { assessmentHistory: globalHistory, currentAssessment } = useAnalysisStore();

  const historyToUse = data || globalHistory;

  // Find all assessments for the current URL
  const currentUrl = overrideUrl || currentAssessment?.article.url || (data && data.length > 0 ? data[0].url : undefined);
  
  const relatedAssessments = useMemo(() => {
    if (!currentUrl) return [];
    // If data is provided explicitly, we assume it's already filtered for the source, 
    // but we still filter by URL to be safe in case data is global but an overrideUrl is given.
    return historyToUse.filter(item => item.url === currentUrl);
  }, [historyToUse, currentUrl]);

  // Group by provider to get the latest for each
  const comparisonData = useMemo(() => {
    const latest: Record<string, AssessmentHistoryItem> = {};
    relatedAssessments.forEach(item => {
      if (!latest[item.provider] || item.timestamp > latest[item.provider].timestamp) {
        latest[item.provider] = item;
      }
    });
    return Object.values(latest);
  }, [relatedAssessments]);

  if (!currentUrl) {
    return (
      <Box p={3}>
        <Alert severity="info">
          Select an article with a URL from the history to see comparative scores across different agents.
        </Alert>
      </Box>
    );
  }

  if (comparisonData.length === 0) {
    return (
      <Box p={3}>
        <Typography variant="h6" gutterBottom>Compare Results</Typography>
        <Alert severity="info">
          No assessments found for this URL yet. Run an assessment to see comparative scores across different providers.
        </Alert>
      </Box>
    );
  }

  if (comparisonData.length === 1) {
    return (
      <Box p={3}>
        <Typography variant="h6" gutterBottom>Compare Results</Typography>
        <Alert severity="warning">
          Only one provider (<strong>{comparisonData[0]?.provider || 'unknown'}</strong>) has assessed this URL. 
          Run an assessment with a different provider to see a side-by-side comparison.
        </Alert>
      </Box>
    );
  }

  const metrics = [
    { label: 'Final Score', key: 'final', isScore: true },
    { label: 'Rating', key: 'rating', isRating: true },
    { label: 'Quality (30%)', key: 'quality', isScore: true },
    { label: 'Veracity (30%)', key: 'veracity_final', isScore: true },
    { label: 'Valence (20%)', key: 'valence', isScore: true },
    { label: 'Manipulation (20%)', key: 'manipulation', isScore: true },
  ];

  const ratingColor = (rating: string) => {
    if (rating === 'GREEN') return 'success';
    if (rating === 'RED') return 'error';
    return 'warning';
  };

  const minComparisonTableWidth = 200 + comparisonData.length * 120;

  return (
    <Box p={{ xs: 1, sm: 2 }}>
      <Typography variant="h6" gutterBottom>
        Agent Comparison
      </Typography>
      <Typography
        variant="caption"
        color="text.secondary"
        display="block"
        gutterBottom
        sx={{ mb: 2, wordBreak: 'break-all' }}
      >
        Comparing latest runs for: {currentUrl}
      </Typography>

      <TableContainer component={Paper} elevation={3} sx={{ overflowX: 'auto' }}>
        <Table size="small" sx={{ minWidth: minComparisonTableWidth }}>
          <TableHead sx={{ bgcolor: 'action.hover' }}>
            <TableRow>
              <TableCell>Metric</TableCell>
              {comparisonData.map(item => (
                <TableCell key={item.id} align="center">
                  <Chip 
                    label={item.provider.toUpperCase()} 
                    color={item.provider === 'gemini' ? 'info' : 'secondary'}
                    size="small"
                  />
                  <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                    {new Date(item.timestamp).toLocaleDateString()}
                  </Typography>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {metrics.map(metric => (
              <TableRow key={metric.label} hover>
                <TableCell component="th" scope="row" sx={{ fontWeight: 500 }}>
                  {metric.label}
                </TableCell>
                {comparisonData.map(item => {
                  const c = computeScores(item.result);
                  const isOpinion = item.result.is_opinion;
                  const computedByKey: Record<string, number | null> = {
                    final: c.finalScore,
                    quality: isOpinion ? null : c.qualityTotal,
                    veracity_final: c.veracityFinal,
                    valence: isOpinion ? null : c.valenceTotal,
                    manipulation: c.manipulationTotal,
                  };
                  let val: any = metric.key in computedByKey
                    ? computedByKey[metric.key]
                    : item.result.scores[metric.key as keyof typeof item.result.scores];

                  // Fallback for fields that might be at the top level or renamed
                  if (val === undefined) {
                    val = (item.result as any)[metric.key];
                  }

                  const renderValue = () => {
                    if (metric.isRating) {
                      return (
                        <Chip 
                          label={item.result.rating === 'YELLOW' ? 'ORANGE' : item.result.rating}
                          color={ratingColor(item.result.rating)} 
                          size="small" 
                        />
                      );
                    }
                    if (typeof val === 'number') {
                      return val.toFixed(1);
                    }
                    return val?.toString() || 'N/A';
                  };
                  
                  return (
                    <TableCell key={item.id} align="center">
                      <Typography variant="body1" fontWeight="bold">
                        {renderValue()}
                      </Typography>
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      
      <Box mt={3}>
        <Typography variant="subtitle2" gutterBottom>Notes:</Typography>
        <Stack spacing={1}>
          {comparisonData.map(item => (
            <Box key={item.id} p={1.5} sx={{ bgcolor: 'background.default', borderRadius: 1, border: 1, borderColor: 'divider' }}>
              <Typography variant="caption" fontWeight="bold" color={item.provider === 'gemini' ? 'info.main' : 'secondary.main'}>
                {item.provider.toUpperCase()} Verdict:
              </Typography>
              <Typography variant="body2" sx={{ mt: 0.5, fontStyle: 'italic' }}>
                "{item.result.verdict.headline}"
              </Typography>
            </Box>
          ))}
        </Stack>
      </Box>
    </Box>
  );
}
